import {
  Checkbox,
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  Input,
} from '@monksflow/monks-ui';
import { useId, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BYTES_PER_MEGABYTE,
  DEFAULT_SECTION_CONFIG,
  FILE_TYPE_GROUPS,
  MAX_FILE_SIZE_MB,
  selectedFileTypeGroupIds,
  toggleFileTypeGroup,
} from '@/entities/brief';

export interface FileConfigFieldsProps {
  config: Record<string, unknown>;
  onChange: (patch: Record<string, unknown>) => void;
  disabled: boolean;
}

const DEFAULT_FILE_CONFIG = DEFAULT_SECTION_CONFIG.file as {
  acceptedMimeTypes: string[];
  maxSizeBytes: number;
};

function parseSizeMb(raw: string): number | null {
  if (raw.trim() === '') return null;
  const mb = Number(raw);
  if (!Number.isFinite(mb)) return null;
  return Math.min(Math.max(Math.round(mb), 1), MAX_FILE_SIZE_MB);
}

/**
 * The size input keeps the author's text while they type, so clearing it to enter a new number
 * does not snap back. Every keystroke writes, clamped to a whole number of MB within range,
 * because the drawer can close with the input still focused and a blur-only write would be
 * lost; clamping rather than rejecting is what keeps `150` from ending at its prefix `15`.
 * Leaving the field shows the stored size again.
 */
export function FileConfigFields(props: FileConfigFieldsProps) {
  const { t } = useTranslation('knowledge');
  const idPrefix = useId();
  const [sizeDraft, setSizeDraft] = useState<string | null>(null);

  const acceptedMimeTypes = Array.isArray(props.config.acceptedMimeTypes)
    ? (props.config.acceptedMimeTypes as string[])
    : DEFAULT_FILE_CONFIG.acceptedMimeTypes;
  const maxSizeBytes =
    typeof props.config.maxSizeBytes === 'number'
      ? props.config.maxSizeBytes
      : DEFAULT_FILE_CONFIG.maxSizeBytes;
  const maxCount = props.config.maxCount;
  const selectedGroups = selectedFileTypeGroupIds(acceptedMimeTypes);
  const storedSizeMb = String(Math.round(maxSizeBytes / BYTES_PER_MEGABYTE));
  const sizeHintId = `${idPrefix}-max-size-hint`;

  const handleSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSizeDraft(event.target.value);
    const mb = parseSizeMb(event.target.value);
    if (mb !== null) {
      props.onChange({ maxSizeBytes: mb * BYTES_PER_MEGABYTE });
    }
  };

  return (
    <div className="flex flex-col gap-md">
      <FieldSet>
        <FieldLegend variant="label">
          {t('knowledgeHub.briefSectionConfigFields.allowedFileTypes')}
        </FieldLegend>
        <div className="grid grid-cols-2 gap-sm">
          {FILE_TYPE_GROUPS.map((group) => {
            const checkboxId = `${idPrefix}-${group.id}`;
            const hintId = `${checkboxId}-hint`;
            const isSelected = selectedGroups.includes(group.id);
            const isPartial =
              !isSelected && group.types.some((type) => acceptedMimeTypes.includes(type.mimeType));
            const isLastAccepted =
              isSelected && toggleFileTypeGroup(acceptedMimeTypes, group.id, false).length === 0;
            return (
              <Field key={group.id} orientation="horizontal">
                <Checkbox
                  id={checkboxId}
                  aria-describedby={hintId}
                  checked={isSelected ? true : isPartial ? 'indeterminate' : false}
                  disabled={props.disabled || isLastAccepted}
                  onCheckedChange={(checked: boolean | 'indeterminate') =>
                    props.onChange({
                      acceptedMimeTypes: toggleFileTypeGroup(
                        acceptedMimeTypes,
                        group.id,
                        checked === true,
                      ),
                    })
                  }
                />
                <FieldContent>
                  <FieldLabel htmlFor={checkboxId}>{t(group.labelKey)}</FieldLabel>
                  <FieldDescription id={hintId}>
                    {group.types.flatMap((type) => type.extensions).join(', ')}
                  </FieldDescription>
                </FieldContent>
              </Field>
            );
          })}
        </div>
      </FieldSet>

      <div className="grid grid-cols-2 gap-sm">
        <Field>
          <FieldLabel htmlFor={`${idPrefix}-max-size`}>
            {t('knowledgeHub.briefSectionConfigFields.maxFileSize')}
          </FieldLabel>
          <Input
            id={`${idPrefix}-max-size`}
            type="number"
            min={1}
            max={MAX_FILE_SIZE_MB}
            step={1}
            aria-describedby={sizeHintId}
            disabled={props.disabled}
            value={sizeDraft ?? storedSizeMb}
            onChange={handleSizeChange}
            onBlur={() => setSizeDraft(null)}
          />
          <FieldDescription id={sizeHintId}>
            {t('knowledgeHub.briefSectionConfigFields.maxFileSizeHint', {
              max: MAX_FILE_SIZE_MB,
            })}
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor="brief-section-config-max-count">
            {t('knowledgeHub.briefSectionConfigFields.maxCount')}
          </FieldLabel>
          <Input
            id="brief-section-config-max-count"
            type="number"
            min={1}
            disabled={props.disabled}
            value={typeof maxCount === 'number' ? maxCount : 1}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const raw = Number(event.target.value);
              props.onChange({ maxCount: Number.isFinite(raw) && raw > 0 ? raw : 1 });
            }}
          />
        </Field>
      </div>
    </div>
  );
}
